import Seo, { breadcrumb } from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { CoveButton } from "@/components/CoveButton";
import { ArchImage } from "@/components/ArchImage";
import { Reveal, FloatingBlob } from "@/components/motion";
import { FAQS, IMAGES } from "@/data/content";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function FAQ() {
  return (
    <>
      <Seo
        title="Frequently Asked Questions"
        description="Answers to common questions about craniosacral therapy at Cranio Cove, what to wear, session length, whether it's massage, and how to book with Laura McLelland."
        path="/faq"
        schema={faqSchema}
      />
      <PageHero
        overline="Frequently asked questions"
        titleLines={["A little more", "clarity"]}
        intro="Everything you might want to know before your first visit. If your question isn't here, you're always welcome to reach out."
        breadcrumb={breadcrumb([{ name: "Home", path: "/" }, { name: "FAQ", path: "/faq" }])}
        heroImage={{ src: IMAGES.coveClouds, alt: "A glassy lake mirroring soft clouds and a distant tree line" }}
      />

      <section className="relative px-6 md:px-12 py-12 md:py-20 overflow-hidden" data-testid="faq-list">
        <FloatingBlob color="blue" size={320} className="top-24 -right-16" delay={0.2} />
        <div className="relative mx-auto max-w-4xl">
          <Reveal>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-cove-slate/15 px-4 rounded-2xl transition-colors duration-300 hover:bg-cove-blush/20 data-[state=open]:bg-cove-blush/25"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="py-7 hover:no-underline text-left font-serif text-2xl md:text-3xl text-cove-slate [&>svg]:h-5 [&>svg]:w-5 [&>svg]:text-cove-rose">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-7">
                    <p className="text-lg leading-relaxed text-cove-olive max-w-3xl">{f.a}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-28 md:pb-40" data-testid="faq-cta">
        <div className="mx-auto max-w-[1400px] text-center">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-6xl leading-[0.95] text-cove-slate">Still wondering?</h2>
            <p className="mt-6 max-w-lg mx-auto text-cove-olive leading-relaxed">
              Send Laura a message with anything on your mind, no question is too small.
            </p>
            <div className="mt-10">
              <CoveButton to="/contact" variant="primary" testId="faq-contact-button">Get in Touch</CoveButton>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
